import { jsPDF } from "jspdf";
import axios from "axios";

// Saturn Styling brand palette
const BRAND_ORANGE = "#ff6b35";
const BRAND_DARK = "#0f172a";
const MUTED = "#64748b";

const fetchImageAsDataUrl = async (url) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(res.data);
    });
  } catch {
    return null;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-IE", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const drawHeader = (doc, pageWidth, variantLabel) => {
  doc.setFillColor(BRAND_ORANGE);
  doc.rect(0, 0, pageWidth, 22, "F");

  doc.setTextColor("#ffffff");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("SATURN STYLING", 12, 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Premium Automotive Tinting & Styling", 12, 15.5);

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(variantLabel, pageWidth - 12, 12, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("saturnstyling.ie", pageWidth - 12, 17, { align: "right" });
};

const drawFooter = (doc, pageWidth, pageHeight, pageNum, pageCount, variantLabel) => {
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text(
    `Saturn Styling • ${variantLabel} • Generated ${new Date().toLocaleString("en-IE")}`,
    12,
    pageHeight - 7
  );
  doc.text(`Page ${pageNum} of ${pageCount}`, pageWidth - 12, pageHeight - 7, { align: "right" });
};

const ensureSpace = (doc, pageWidth, y, need = 30, variantLabel = "JOB CARD") => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + need > pageHeight - 15) {
    doc.addPage();
    drawHeader(doc, pageWidth, variantLabel);
    return 30;
  }
  return y;
};

const sectionTitle = (doc, pageWidth, y, title, variantLabel) => {
  y = ensureSpace(doc, pageWidth, y, 12, variantLabel);
  doc.setFillColor(BRAND_DARK);
  doc.rect(12, y, pageWidth - 24, 7, "F");
  doc.setTextColor("#ffffff");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(title.toUpperCase(), 14, y + 5);
  doc.setTextColor("#000000");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  return y + 11;
};

const keyVal = (doc, x, y, label, value) => {
  doc.setTextColor(MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(label.toUpperCase(), x, y);
  doc.setTextColor("#000000");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(value || "—"), x, y + 5);
  doc.setFont("helvetica", "normal");
};

/**
 * variant: "merchant" (full internal) | "customer" (clean customer-facing)
 */
export const generateJobCardPDF = async ({ booking, jobLog, variant = "merchant", photoUrl }) => {
  const isCustomer = variant === "customer";
  const variantLabel = isCustomer ? "CUSTOMER COPY" : "MERCHANT COPY";

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  drawHeader(doc, pageWidth, variantLabel);
  let y = 30;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor("#000000");
  doc.text(booking.customer_name || "Customer", 12, y);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(MUTED);
  if (!isCustomer) {
    doc.text(`Booking ID: ${booking.booking_id || "—"}`, pageWidth - 12, y, { align: "right" });
  } else {
    doc.text("Thank you for choosing Saturn Styling", pageWidth - 12, y, { align: "right" });
  }
  y += 8;

  // Vehicle & Customer
  y = sectionTitle(doc, pageWidth, y, "Vehicle & Customer", variantLabel);
  const col1 = 14, col2 = pageWidth / 2 + 5;
  keyVal(doc, col1, y, "Customer", booking.customer_name);
  keyVal(doc, col2, y, "Phone", booking.customer_phone);
  y += 12;
  keyVal(doc, col1, y, "Vehicle", `${booking.vehicle?.year || ""} ${booking.vehicle?.make || ""} ${booking.vehicle?.model || ""}`.trim());
  keyVal(doc, col2, y, "Registration", booking.vehicle_reg || booking.vehicle?.reg || jobLog?.vehicle_reg);
  y += 12;
  keyVal(doc, col1, y, "Booking Date", `${booking.booking_date || ""} ${booking.booking_time || ""}`.trim());
  keyVal(doc, col2, y, "Warranty", booking.warranty_duration || jobLog?.warranty_duration);
  y += 14;

  // Services
  y = sectionTitle(doc, pageWidth, y, "Services Performed", variantLabel);
  const services = (jobLog?.service_names?.length ? jobLog.service_names : null)
    || booking.services_details?.map(s => s.name) || [];
  doc.setFontSize(10);
  doc.setTextColor("#000000");
  const servicesText = services.length ? services.join(", ") : "—";
  const svcLines = doc.splitTextToSize(servicesText, pageWidth - 28);
  doc.text(svcLines, 14, y);
  y += svcLines.length * 5 + 4;
  if (jobLog?.shade_variant) {
    doc.setTextColor(MUTED);
    doc.setFontSize(9);
    doc.text("Tint Shade Applied: ", 14, y);
    doc.setTextColor("#000000");
    doc.setFont("helvetica", "bold");
    doc.text(jobLog.shade_variant, 44, y);
    doc.setFont("helvetica", "normal");
    y += 6;
  }
  y += 4;

  // === MERCHANT-ONLY internal sections ===
  if (!isCustomer) {
    // Completion Details
    y = sectionTitle(doc, pageWidth, y, "Completion Details", variantLabel);
    keyVal(doc, col1, y, "Technician", jobLog?.technician_name);
    keyVal(doc, col2, y, "Duration", jobLog?.duration_minutes
      ? `${Math.floor(jobLog.duration_minutes / 60)}h ${jobLog.duration_minutes % 60}m`
      : null);
    y += 12;
    keyVal(doc, col1, y, "Completed", formatDate(jobLog?.end_time || booking.completed_at));
    keyVal(doc, col2, y, "Checked In", formatDate(jobLog?.checked_in_at));
    y += 14;

    // Materials
    const materials = jobLog?.materials_used || [];
    if (materials.length) {
      y = sectionTitle(doc, pageWidth, y, "Materials Used", variantLabel);
      doc.setFontSize(10);
      for (const m of materials) {
        y = ensureSpace(doc, pageWidth, y, 6, variantLabel);
        const name = m.product_name || m.item_name || "Material";
        const amount = m.amount_display ||
          (m.amount_used ? `${m.amount_used} ${m.base_unit || 'units'}` :
           (m.quantity !== undefined ? `${m.quantity} ${m.unit || 'units'}` : "—"));
        doc.setTextColor("#000000");
        doc.text(`• ${name}`, 16, y);
        doc.setTextColor(BRAND_ORANGE);
        doc.setFont("helvetica", "bold");
        doc.text(String(amount), pageWidth - 14, y, { align: "right" });
        doc.setFont("helvetica", "normal");
        y += 6;
      }
      y += 2;
    }

    // Notes
    if (jobLog?.notes) {
      y = sectionTitle(doc, pageWidth, y, "Notes", variantLabel);
      doc.setFontSize(10);
      doc.setTextColor("#000000");
      const lines = doc.splitTextToSize(jobLog.notes, pageWidth - 28);
      for (const line of lines) {
        y = ensureSpace(doc, pageWidth, y, 6, variantLabel);
        doc.text(line, 14, y);
        y += 5;
      }
      y += 4;
    }

    // Damage records
    const damages = jobLog?.damage_records || [];
    if (damages.length) {
      y = sectionTitle(doc, pageWidth, y, "Damage Records", variantLabel);
      for (const d of damages) {
        y = ensureSpace(doc, pageWidth, y, 14, variantLabel);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor("#000000");
        doc.text(d.location || "—", 14, y);
        if (d.acknowledged_by_customer) {
          doc.setTextColor("#16a34a");
          doc.setFontSize(8);
          doc.text("SIGNED", pageWidth - 14, y, { align: "right" });
        }
        if (d.recorded_during_work) {
          doc.setTextColor("#ea580c");
          doc.setFontSize(8);
          doc.text("DURING WORK", pageWidth - 32, y, { align: "right" });
        }
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor("#334155");
        const dlines = doc.splitTextToSize(d.description || "—", pageWidth - 28);
        for (const line of dlines) {
          y = ensureSpace(doc, pageWidth, y, 5, variantLabel);
          doc.text(line, 16, y);
          y += 4;
        }
        y += 4;
      }
    }

    // Signature
    if (jobLog?.damage_sign_off?.signature) {
      y = sectionTitle(doc, pageWidth, y, "Customer Acknowledgment", variantLabel);
      y = ensureSpace(doc, pageWidth, y, 40, variantLabel);
      doc.setFontSize(9);
      doc.setTextColor(MUTED);
      doc.text(`Signed by: ${jobLog.damage_sign_off.customer_name || "—"}`, 14, y);
      doc.text(`At: ${formatDate(jobLog.damage_sign_off.signed_at)}`, pageWidth - 14, y, { align: "right" });
      y += 4;
      try {
        doc.addImage(jobLog.damage_sign_off.signature, "PNG", 14, y, 60, 22);
      } catch {
        // ignore
      }
      y += 26;
    }
  }

  // Photos section (both variants get photos)
  const beforePhotos = jobLog?.before_photos || [];
  const afterPhotos = jobLog?.after_photos || [];

  const addPhotoGrid = async (title, photos) => {
    if (!photos.length) return;
    if (title) y = sectionTitle(doc, pageWidth, y, title, variantLabel);
    const cols = 3;
    const gap = 4;
    const totalGap = gap * (cols - 1);
    const boxW = (pageWidth - 28 - totalGap) / cols;
    const boxH = boxW * 0.75;
    let col = 0;
    for (const p of photos) {
      if (col === 0) {
        y = ensureSpace(doc, pageWidth, y, boxH + 4, variantLabel);
      }
      const x = 14 + col * (boxW + gap);
      const dataUrl = await fetchImageAsDataUrl(photoUrl(p.photo_id));
      try {
        if (dataUrl) {
          // Detect image format from data URL prefix so PNGs render correctly
          let fmt = "JPEG";
          const m = /^data:image\/([a-zA-Z0-9+.-]+);/i.exec(dataUrl);
          if (m) {
            const t = m[1].toLowerCase();
            if (t === "png") fmt = "PNG";
            else if (t === "webp") fmt = "WEBP";
            else fmt = "JPEG";
          }
          doc.addImage(dataUrl, fmt, x, y, boxW, boxH, undefined, "FAST");
        } else {
          doc.setFillColor("#e2e8f0");
          doc.rect(x, y, boxW, boxH, "F");
        }
      } catch {
        doc.setFillColor("#e2e8f0");
        doc.rect(x, y, boxW, boxH, "F");
      }
      col++;
      if (col >= cols) {
        col = 0;
        y += boxH + gap;
      }
    }
    if (col > 0) y += boxH + gap;
    y += 4;
  };

  await addPhotoGrid("Before Photos", beforePhotos);
  await addPhotoGrid("After Photos", afterPhotos);

  // Merchant-only: damage photos
  if (!isCustomer) {
    const damages = jobLog?.damage_records || [];
    if (damages.some(d => (d.photos || []).length > 0)) {
      y = sectionTitle(doc, pageWidth, y, "Damage Photos", variantLabel);
      for (const d of damages) {
        if (!d.photos?.length) continue;
        y = ensureSpace(doc, pageWidth, y, 10, variantLabel);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#000000");
        doc.text(d.location || "—", 14, y);
        doc.setFont("helvetica", "normal");
        y += 4;
        await addPhotoGrid("", d.photos);
      }
    }
  }

  // Customer-only: Thank-you & aftercare note block
  if (isCustomer) {
    y = ensureSpace(doc, pageWidth, y, 40, variantLabel);
    y = sectionTitle(doc, pageWidth, y, "Thank You", variantLabel);
    doc.setFontSize(10);
    doc.setTextColor("#000000");
    const msg = "Thank you for choosing Saturn Styling. Please follow the aftercare guidance provided with your collection email to get the best from your finish. We look forward to seeing you again!";
    const msgLines = doc.splitTextToSize(msg, pageWidth - 28);
    for (const line of msgLines) {
      y = ensureSpace(doc, pageWidth, y, 6, variantLabel);
      doc.text(line, 14, y);
      y += 5;
    }
  }

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawFooter(doc, pageWidth, pageHeight, i, pageCount, variantLabel);
  }

  const reg = booking.vehicle_reg || booking.vehicle?.reg || jobLog?.vehicle_reg || "unknown";
  const suffix = isCustomer ? "Customer" : "Merchant";
  const filename = `SaturnStyling_JobCard_${suffix}_${reg}_${booking.booking_id || "job"}.pdf`;
  doc.save(filename);
};
